/**
 * Trunnion AI - Execution Monitor Store
 * Manages real-time execution monitoring UI state.
 */

import { create } from 'zustand';
import type { ExecutionStatus } from '@/types';

interface StepState {
  id: string;
  nodeId: string;
  nodeName: string;
  nodeType: string;
  status: ExecutionStatus;
  agentId?: string;
  agentName?: string;
  startedAt: string;
  completedAt?: string;
  output?: Record<string, unknown>;
  error?: string;
  toolCalls: ToolCallState[];
  reasoningEntries: ReasoningEntryState[];
  tokenUsage?: TokenUsageState;
}

interface ToolCallState {
  id: string;
  toolId: string;
  toolName: string;
  status: string;
  parameters?: Record<string, unknown>;
  result?: Record<string, unknown>;
  error?: string;
  startedAt?: string;
  completedAt?: string;
}

interface ReasoningEntryState {
  id: string;
  type: string;
  content: string;
  timestamp: string;
}

interface TokenUsageState {
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  estimatedCost: number;
}

interface ExecutionMonitorState {
  executionId: string | null;
  steps: StepState[];
  activeStepId: string | null;
  selectedStepId: string | null;
  isFollowMode: boolean;
  showReasoningTrace: boolean;
  showToolCalls: boolean;

  setExecutionId: (id: string | null) => void;
  addStep: (step: Omit<StepState, 'toolCalls' | 'reasoningEntries'>) => void;
  updateStep: (stepId: string, updates: Partial<StepState>) => void;
  addToolCall: (stepId: string, toolCall: ToolCallState) => void;
  addReasoningEntry: (stepId: string, entry: ReasoningEntryState) => void;
  updateTokenUsage: (stepId: string, usage: TokenUsageState) => void;
  setSelectedStepId: (id: string | null) => void;
  setFollowMode: (follow: boolean) => void;
  setShowReasoningTrace: (show: boolean) => void;
  setShowToolCalls: (show: boolean) => void;
  clearMonitor: () => void;
}

export const useExecutionMonitorStore = create<ExecutionMonitorState>(
  (set, get) => ({
    executionId: null,
    steps: [],
    activeStepId: null,
    selectedStepId: null,
    isFollowMode: true,
    showReasoningTrace: true,
    showToolCalls: true,

    setExecutionId: (id) =>
      set({ executionId: id, steps: [], activeStepId: null, selectedStepId: null }),

    addStep: (step) =>
      set({
        steps: [
          ...get().steps,
          { ...step, toolCalls: [], reasoningEntries: [] },
        ],
        activeStepId: step.id,
        ...(get().isFollowMode && { selectedStepId: step.id }),
      }),

    updateStep: (stepId, updates) =>
      set({
        steps: get().steps.map((s) =>
          s.id === stepId ? { ...s, ...updates } : s,
        ),
        ...(updates.status === 'completed' || updates.status === 'failed'
          ? { activeStepId: null }
          : {}),
      }),

    addToolCall: (stepId, toolCall) =>
      set({
        steps: get().steps.map((s) =>
          s.id === stepId
            ? {
                ...s,
                toolCalls: [
                  ...s.toolCalls.filter((tc) => tc.id !== toolCall.id),
                  toolCall,
                ],
              }
            : s,
        ),
      }),

    addReasoningEntry: (stepId, entry) =>
      set({
        steps: get().steps.map((s) =>
          s.id === stepId
            ? { ...s, reasoningEntries: [...s.reasoningEntries, entry] }
            : s,
        ),
      }),

    updateTokenUsage: (stepId, usage) =>
      set({
        steps: get().steps.map((s) =>
          s.id === stepId ? { ...s, tokenUsage: usage } : s,
        ),
      }),

    setSelectedStepId: (id) => set({ selectedStepId: id }),

    setFollowMode: (follow) => set({ isFollowMode: follow }),

    setShowReasoningTrace: (show) => set({ showReasoningTrace: show }),

    setShowToolCalls: (show) => set({ showToolCalls: show }),

    clearMonitor: () =>
      set({
        executionId: null,
        steps: [],
        activeStepId: null,
        selectedStepId: null,
      }),
  }),
);
